import { OverlayPage } from '@components/partials/OverlayPage';
import {
  DownloadIcon,
  Loader,
  CalendarIcon,
  ArrowLongRightIcon,
  ReportHeader,
} from '@cloudclevr/components';
import {
  useReportPdfUrlPreviewQuery,
  useReportPreviewQuery,
} from '@hooks/reports';
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import styles from './styles.module.css';
import {
  ChartComponent,
  ReportChartData,
  ReportSelectedVendors,
} from '@hooks/reports/types';
import {} from '@pages/InsightsAndAnalytics/tiles/LineChartTile';
import { hasPermission, formatDateInUTC } from '@utils/index';
import { UserRoles, Permissions } from '@hooks/users/types';
import useAuthStore from '@store/authStore';
import RenderChartTile from './RenderChartTile';
import { ReportV2Preview } from './ReportV2Preview';
import type { ReportDocument } from '@cloudclevr/components';
import axios from 'axios';
import dayjs from 'dayjs';
import useUIStore from '@store/uiStore';
import { Variant } from '@components/ui/Toast';

export const ReportPreviewPage: React.FC = () => {
  const [selectedVendors, setSelectedVendors] = useState<ReportSelectedVendors>(
    {}
  );
  const [pdfUrl, setPdfUrl] = useState('');

  let chartData: ReportChartData | null = null;

  const { reportId } = useParams<{ reportId: string }>();

  const query = useReportPreviewQuery({
    reportId: reportId,
  });

  const { mutateAsync, isPending: isPdfUrlLoading } =
    useReportPdfUrlPreviewQuery(reportId);

  if (query.isFetched) {
    if (query.data?.jsonReportData) {
      chartData = JSON.parse(query.data?.jsonReportData);
    }
  }

  const isV2Report = chartData?.version === '2.0';

  const reportTypeName = query.data?.name?.substring(12).trim() ?? '';
  const headerTitle = isV2Report && query.data?.organizationName
    ? `${query.data.organizationName}: ${reportTypeName}`
    : query.data?.name || '';

  const userRole = useAuthStore.getState().role || UserRoles.STANDARD;

  const downloadHasPermission = hasPermission(
    userRole,
    Permissions.ADMINS_SUPPORTUSER
  );

  useEffect(() => {
    if (query.isFetched && (!isV2Report || chartData?.hasPdf)) {
      getPdfUrl();
    }
  }, [query.isFetched, isV2Report]);

  const { addToast, setSidebar, overlayHeaderHeight } = useUIStore();
  const rendererContainerRef = useRef<HTMLDivElement>(null);

  // Handle Contents → ReportRenderer chapter/section navigation messages.
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type !== 'REPORT_SCROLL_TO_ITEM') return;
      const { chapterIndex, sectionIndex } = event.data.payload ?? {};
      if (chapterIndex == null) return;

      const container = rendererContainerRef.current;
      if (!container) return;

      let target: Element | null = null;
      if (sectionIndex != null) {
        const chapterEl = container.querySelector(`#chapter-${chapterIndex}`);
        if (chapterEl) {
          target = chapterEl.querySelectorAll('[data-type="Section"]')[sectionIndex] ?? null;
        }
      } else {
        target = container.querySelector(`#chapter-${chapterIndex}`);
      }
      if (!target) return;

      const scrollEl =
        container.scrollHeight > container.clientHeight
          ? container
          : findScrollableAncestor(container);
      if (!scrollEl) return;

      const delta =
        (target as HTMLElement).getBoundingClientRect().top -
        scrollEl.getBoundingClientRect().top;

      scrollEl.scrollTo({ top: scrollEl.scrollTop + delta });
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  useEffect(() => {
    if (chartData && !isV2Report) {
      const initialVendors: ReportSelectedVendors = {};
      chartData.components.forEach((component: ChartComponent) => {
        initialVendors[component.type] = 'All';
      });
      setSelectedVendors(initialVendors);
    }
  }, [query.isFetched]);

  const getPdfUrl = async () => {
    if (downloadHasPermission) {
      const res = await mutateAsync();
      setPdfUrl(res as string);
    }
  };

  const downloadFile = async () => {
    try {
      const date = query.data?.name.substring(0, 11);
      const reportName = query.data?.name.substring(12).trim() ?? 'report';

      const name = reportName
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join('');

      const response = await axios({
        url: pdfUrl,
        method: 'GET',
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `Clevr360_${name}_${dayjs(date, 'MMM DD YYYY').format(
          'DD-MMM-YYYY'
        )}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      addToast({
        variant: Variant.Error,
        message: `Failed to download the file: ${error}`,
      });
    }
  };

  return (
    <OverlayPage
      isFooterVisible={false}
      path="/reports"
      headerTitle={headerTitle}
      isLoading={query.isLoading}
      overlayClassNames=" !pl-10"
      contentClassNames={isV2Report ? '!p-0 !m-0 flex-1 min-h-0 flex flex-col' : '!pt-0'}
    >
      {query.isLoading ? (
        <Loader />
      ) : isV2Report && chartData ? (
        <>
          {/* Pinned header bar for V2 — never scrolls */}
          {(chartData?.hasPdf || query.data?.createdAt || chartData?.fromDate) && (
            <div className="flex-shrink-0 bg-interfaceColor-5 border-b border-gray-200 pl-2 pb-3 pt-2 ml-1">
              <div className="pl-5">
              <ReportHeader
                fromDate={chartData?.fromDate}
                toDate={chartData?.toDate}
                publishedDate={query.data?.createdAt}
                hasPdf={chartData?.hasPdf && downloadHasPermission}
                onDownload={downloadFile}
              />
              </div>
            </div>
          )}
          {/* Contents + ReportRenderer — only the renderer scrolls */}          
          <ReportV2Preview
            document={chartData as unknown as ReportDocument}
            rendererRef={rendererContainerRef}
          />
        </>
      ) : (
        <>
          {/* Only show ReportHeader if hasPdf is true or publishedDate is not null */}
          {(chartData?.hasPdf || query.data?.createdAt) && (
            <div
              className="sticky z-10 bg-interfaceColor-5 pl-2 pb-2 mb-3"
              style={{ top: overlayHeaderHeight || 0 }}
            >
              <ReportHeader
                publishedDate={query.data?.createdAt}
                hasPdf={chartData?.hasPdf && downloadHasPermission}
                onDownload={downloadFile}
              />
            </div>
          )}
          {chartData?.fromDate !== undefined &&
            chartData?.toDate !== undefined && (
              <h4
                className="mb-4 flex items-center gap-x-1"
                style={{
                  fontSize: '16px',
                  breakInside: 'avoid',
                }}
              >
                <CalendarIcon
                  color="text-interfaceColor-100"
                  size="6"
                  classNames="ml-1"
                />
                {formatDateInUTC(chartData?.fromDate)}
                <ArrowLongRightIcon color="text-interfaceColor-100" size="5" />
                {formatDateInUTC(chartData?.toDate)}
              </h4>
            )}
          {downloadHasPermission && !isPdfUrlLoading && (
            <button
              className="inline-flex flex-shrink-0 items-center justify-center rounded-[24px] bg-interfaceColor-90 px-[16px] py-[10px] text-16 font-normal leading-6 tracking-[0.01em] text-white transition duration-300 ease-in-out hover:bg-interfaceColor-150 hover:text-interfaceColor-10 focus:shadow-focus disabled:bg-interfaceColor-20 disabled:text-interfaceColor-80"
              onClick={() => downloadFile()}
            >
              {' '}
              <DownloadIcon
                classNames="mr-2"
                color="text-primaryBrandColor-150"
              />
              Download report
            </button>
          )}
          {query.isFetched && chartData && !query.isLoading && (
            <div className={styles.pageGrid}>
              {chartData.components.map((item) => {
                return (
                  <RenderChartTile
                    key={item.type}
                    item={item}
                    selectedVendor={selectedVendors}
                  />
                );
              })}
            </div>
          )}
        </>
      )}
    </OverlayPage>
  );
};

/** Walk up the DOM to find the nearest element that actually scrolls. */
function findScrollableAncestor(el: HTMLElement): HTMLElement | null {
  let parent = el.parentElement;
  while (parent && parent !== document.documentElement) {
    const style = getComputedStyle(parent);
    const overflowY = style.overflowY || style.overflow;
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      parent.scrollHeight > parent.clientHeight
    ) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return document.documentElement as HTMLElement;
}
